/**
 * advance-ai-team-claims.ts — v4M8.7a Team-aware AI target-claim
 * bookkeeping per `docs/AI.md §5.8`.
 *
 * Coordinated combat dispatch: when one teammate AI dispatches a
 * combat task force to a system, it pushes a claim into
 * `state.teamClaims`; other teammates' pickers subtract
 * `teammatePenalty × claim-count` from candidate target scores, so
 * the team spreads its attention across different enemies instead
 * of piling on one target.
 *
 * Two expiry paths:
 *   1. **TTL timeout** (this file's `expireTeamClaims`) — drops
 *      claims older than the claiming empire's archetype
 *      `claimTtlTicks`. Runs every tick.
 *   2. **Contact expiry** (handled by `clearTeamClaim` from
 *      v4M8.7b at arrival time) — drops a claim when the claiming
 *      fleet actually arrives at the target system.
 *
 * Runtime-only state. `state.teamClaims` is NOT serialised by
 * design (claims are ephemeral; AI re-derives within ~20 s of play
 * after load). Optional field — `?? new Map()` it at every read.
 *
 * Implementation note: TTL is read directly from
 * `state.aiBehaviours.archetypes[empire.aiArchetype].claimTtlTicks`
 * rather than via `composeEffectiveBehaviour`. The composed
 * behaviour passes `archetype.claimTtlTicks` through unchanged
 * (no difficulty / race multiplier on the field), and reading the
 * raw archetype row avoids a circular import in v4M8.7b when
 * `advance-ai.ts` will import `pushTeamClaim` back from this file.
 *
 * v4M8.7a strict-parity: helpers exist + expiry pass wired into
 * `advanceTick`, but no consumer emits or reads claims yet.
 * v4M8.7b adds emission + contact expiry; v4M8.7c folds the
 * teammate penalty into the picker score.
 */

import type {
  AiTargetClaim,
  Empire,
  GameState,
  SystemId,
  TeamId,
} from '../state/types/index';

/** Defensive fallback if an empire's archetype is missing from the
 *  loaded behaviour table. Matches the fallback shape used by
 *  `composeEffectiveBehaviour` in `advance-ai.ts`. */
const FALLBACK_CLAIM_TTL_TICKS = 200;

function claimTtlTicksForEmpire(state: GameState, empire: Empire): number {
  const row = state.aiBehaviours.archetypes[empire.aiArchetype];
  if (!row) return FALLBACK_CLAIM_TTL_TICKS;
  const ttl = row.claimTtlTicks;
  return Number.isFinite(ttl) && ttl > 0 ? ttl : FALLBACK_CLAIM_TTL_TICKS;
}

/**
 * Expire team claims whose TTL has elapsed. Mutates
 * `state.teamClaims` in place; safe when the map is undefined or
 * empty (early-returns without allocating). Per-tick pass — wired
 * into `advanceTick` ahead of `advanceAi` so each tick's claim
 * reads start from a freshly-filtered set.
 *
 * TTL is the claiming empire's archetype `claimTtlTicks`. Looking
 * up per-claim means a single team with mixed-archetype teammates
 * honours each member's own TTL — Defender claims linger longer
 * than Conqueror claims, even on the same team. A small per-tick
 * cache short-circuits the map lookup when the same empire owns
 * many claims.
 */
export function expireTeamClaims(state: GameState): void {
  if (!state.teamClaims || state.teamClaims.size === 0) return;
  const now = state.tick;
  const ttlCache = new Map<number, number>();

  for (const [teamId, claims] of state.teamClaims) {
    if (claims.length === 0) continue;
    const kept: AiTargetClaim[] = [];
    for (const claim of claims) {
      const empire = state.empires.get(claim.empireId);
      // Claiming empire gone (eliminated) — drop the claim. No way
      // to look up its TTL anyway.
      if (!empire) continue;
      let ttl = ttlCache.get(empire.id);
      if (ttl === undefined) {
        ttl = claimTtlTicksForEmpire(state, empire);
        ttlCache.set(empire.id, ttl);
      }
      if (now - claim.claimedAtTick <= ttl) {
        kept.push(claim);
      }
    }
    if (kept.length === 0) {
      state.teamClaims.delete(teamId);
    } else {
      state.teamClaims.set(teamId, kept);
    }
  }
}

/**
 * Push a new claim into `state.teamClaims` for the empire's team.
 * No-op for solo empires (teamId === null) — coordinated dispatch
 * only matters within a team. Lazily allocates the top-level Map
 * and the per-team array on first claim per team.
 *
 * Called from v4M8.7b — `dispatchCombatJumps` emits one claim per
 * task force on issue.
 */
export function pushTeamClaim(
  state: GameState,
  empire: Empire,
  systemId: SystemId,
): void {
  if (empire.teamId === null) return;
  if (!state.teamClaims) state.teamClaims = new Map();
  let arr = state.teamClaims.get(empire.teamId);
  if (!arr) {
    arr = [];
    state.teamClaims.set(empire.teamId, arr);
  }
  arr.push({
    empireId: empire.id,
    systemId,
    claimedAtTick: state.tick,
  });
}

/**
 * Drop all claims for `(empireId, systemId)` — contact expiry.
 * Called from v4M8.7b's jump-arrival hook when the claiming fleet
 * actually reaches its target system. Walks every team's claim
 * array (cheap — claim lists stay small) and rewrites with the
 * matching entries removed.
 */
export function clearTeamClaim(
  state: GameState,
  empireId: number,
  systemId: SystemId,
): void {
  if (!state.teamClaims || state.teamClaims.size === 0) return;
  for (const [teamId, claims] of state.teamClaims) {
    const kept = claims.filter(
      (c) => !(c.empireId === empireId && c.systemId === systemId),
    );
    if (kept.length === claims.length) continue;
    if (kept.length === 0) {
      state.teamClaims.delete(teamId);
    } else {
      state.teamClaims.set(teamId, kept);
    }
  }
}

/**
 * Count active claims for `systemId` placed by OTHER empires on
 * `viewer`'s team. Used by v4M8.7c's picker — the picker subtracts
 * `teammatePenalty × this count` from the candidate score.
 *
 * Filters out the viewer's own claims (an empire shouldn't
 * penalise itself for a system it already claimed — its own claim
 * should have already steered the original decision). Solo
 * empires return 0 — no teammates to coordinate with.
 */
export function teammateClaimCountForSystem(
  state: GameState,
  viewer: Empire,
  systemId: SystemId,
): number {
  if (viewer.teamId === null) return 0;
  if (!state.teamClaims) return 0;
  const arr = state.teamClaims.get(viewer.teamId);
  if (!arr || arr.length === 0) return 0;
  let n = 0;
  for (const claim of arr) {
    if (claim.systemId !== systemId) continue;
    if (claim.empireId === viewer.id) continue;
    n++;
  }
  return n;
}

/**
 * Marker re-export — v4M8.7b imports `TeamId` from here so the
 * call sites that emit claims don't have to bring in the full
 * types.ts surface. Internal convenience, no behaviour.
 */
export type { TeamId };
